// The public API (Settings → API & connections): what an API key may read or change, the events Falcon can send to
// other systems (alerts / webhooks), and the addresses listed in the API guide. Shared by the server (which enforces
// them), the settings screen and the Zapier connection (integrations/zapier), so all three always agree.

export const API_VERSION = "v1";

/** What a key may do. Each key gets only the ticks its Admin chose. */
export const API_SCOPES = [
  ["styles:read", "Read styles & Tech Packs"],
  ["pos:read", "Read purchase orders"],
  ["pos:write", "Change PO working ETD / ETA"],
  ["samples:read", "Read samples, lab dips & QC"],
  ["shipments:read", "Read shipments"],
  ["finance:read", "Read finance (bills, payments, PO prices)"],
] as const;
export type ApiScope = (typeof API_SCOPES)[number][0];
export const API_SCOPE_KEYS = API_SCOPES.map(([k]) => k) as ApiScope[];
export const SCOPE_LABEL = Object.fromEntries(API_SCOPES) as Record<ApiScope, string>;
/** Changing PO dates needs reading POs too (a key can't change what it can't see). */
export function normalizeScopes(list: readonly string[]): ApiScope[] {
  const set = new Set(list.filter((s): s is ApiScope => (API_SCOPE_KEYS as string[]).includes(s)));
  if (set.has("pos:write")) set.add("pos:read");
  return API_SCOPE_KEYS.filter((k) => set.has(k));
}

/** Events Falcon can send. `scope` = what a key must be able to read to receive it (alerts added through the API). */
export const API_EVENTS = [
  { key: "sample.approved", label: "Sample approved", scope: "samples:read" },
  { key: "sample.rejected", label: "Sample rejected", scope: "samples:read" },
  { key: "labdip.approved", label: "Lab dip approved", scope: "samples:read" },
  { key: "labdip.rejected", label: "Lab dip rejected", scope: "samples:read" },
  { key: "qc.submitted", label: "QC report submitted (pass / fail)", scope: "samples:read" },
  { key: "po.created", label: "New PO created", scope: "pos:read" },
  { key: "po.issued", label: "PO issued", scope: "pos:read" },
  { key: "po.cancelled", label: "PO cancelled or reopened", scope: "pos:read" },
  { key: "po.stage_changed", label: "PO stage changes (Bulk, Ready to ship…)", scope: "pos:read" },
  { key: "po.dates_changed", label: "PO working ETD / ETA changed", scope: "pos:read" },
  { key: "techpack.approved", label: "Tech Pack approved", scope: "styles:read" },
  { key: "shipment.created", label: "Shipment booked", scope: "shipments:read" },
  { key: "shipment.received", label: "Shipment received", scope: "shipments:read" },
  { key: "payment.made", label: "Supplier payment made", scope: "finance:read" },
] as const satisfies ReadonlyArray<{ key: string; label: string; scope: ApiScope }>;
export type ApiEvent = (typeof API_EVENTS)[number]["key"];
export const API_EVENT_KEYS = API_EVENTS.map((e) => e.key) as ApiEvent[];
export const EVENT_LABEL = Object.fromEntries(API_EVENTS.map((e) => [e.key, e.label])) as Record<ApiEvent, string>;
export const EVENT_SCOPE = Object.fromEntries(API_EVENTS.map((e) => [e.key, e.scope])) as Record<ApiEvent, ApiScope>;

/** Each key may make this many requests a minute, so one busy system can't slow Falcon down for the team. */
export const API_PER_MINUTE = 60;
/** A message that isn't delivered is tried again after these waits (minutes): 5 more tries over about 15 hours. */
export const RETRY_MINUTES = [1, 5, 30, 120, 720];
/** Longest wait for the other system to answer one message. */
export const DELIVERY_TIMEOUT_MS = 10_000;

/** The addresses in the API guide (method, path, what it gives, scope it needs). */
export const API_ENDPOINTS: Array<{ method: "GET" | "POST" | "PATCH" | "DELETE"; path: string; text: string; scope: ApiScope | "" }> = [
  { method: "GET", path: "/api/v1/me", text: "Which organisation and key this is, and what it may do", scope: "" },
  { method: "GET", path: "/api/v1/styles", text: "Styles (garments) with colours and sizes", scope: "styles:read" },
  { method: "GET", path: "/api/v1/styles/{id}", text: "One style with its bill of materials", scope: "styles:read" },
  { method: "GET", path: "/api/v1/techpacks", text: "Tech Packs with status and revision", scope: "styles:read" },
  { method: "GET", path: "/api/v1/purchase-orders", text: "POs with supplier, dates and status", scope: "pos:read" },
  { method: "GET", path: "/api/v1/purchase-orders/{id}", text: "One PO with its lines and stages", scope: "pos:read" },
  { method: "PATCH", path: "/api/v1/purchase-orders/{id}", text: "Change the working ETD / ETA", scope: "pos:write" },
  { method: "GET", path: "/api/v1/samples", text: "Sample rounds and their results", scope: "samples:read" },
  { method: "GET", path: "/api/v1/lab-dips", text: "Lab dip rounds and their results", scope: "samples:read" },
  { method: "GET", path: "/api/v1/qc-reports", text: "Submitted QC reports and results", scope: "samples:read" },
  { method: "GET", path: "/api/v1/shipments", text: "Shipments with dates and status", scope: "shipments:read" },
  { method: "GET", path: "/api/v1/finance/bills", text: "Supplier bills for a date range", scope: "finance:read" },
  { method: "GET", path: "/api/v1/finance/payments", text: "Supplier payments for a date range", scope: "finance:read" },
  { method: "GET", path: "/api/v1/hooks", text: "Alerts this key added", scope: "" },
  { method: "POST", path: "/api/v1/hooks", text: "Add an alert (used by Zapier)", scope: "" },
  { method: "DELETE", path: "/api/v1/hooks/{id}", text: "Remove an alert this key added", scope: "" },
  { method: "GET", path: "/api/v1/events/{event}/sample", text: "A recent example of an event", scope: "" },
];

/** Keys start with flc_live_ (flc_test_ on the test copy) so they're easy to recognise if pasted somewhere by mistake. */
export const KEY_PREFIX = /^flc_(live|test)_[A-Za-z0-9]{32}$/;
export const keyHint = (last4: string) => `flc_…${last4}`;

/** Only https web addresses, at most 500 characters. The server also refuses addresses inside a private network. */
export function webhookUrlProblem(url: string): string | null {
  let u: URL;
  try { u = new URL(url); } catch { return "Enter the full web address, starting with https://"; }
  if (u.protocol !== "https:") return "The address must start with https:// so the message is sent securely.";
  if (u.username || u.password) return "Leave the user name and password out of the address.";
  if (url.length > 500) return "That address is too long.";
  return null;
}

export const DELIVERY_STATUS_LABEL: Record<string, string> = { pending: "Waiting to retry", delivered: "Delivered", failed: "Failed", not_sent: "Not sent" };
